import { Component } from '@theme/component';
import { QuoteAddEvent, QuoteErrorEvent, QuoteUpdateEvent, ThemeEvents } from '@theme/events';
import { formatQuotePrice } from '@theme/quote-pricing';
import { QuoteStorage } from '@theme/quote-storage';
import { onAnimationEnd, preloadImage, yieldToMainThread } from '@theme/utilities';

const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToQuote
 */

/**
 * @typedef {object} AddToQuoteRefs
 * @property {HTMLButtonElement} addToQuoteButton
 * @extends {Component<AddToQuoteRefs>}
 */
export class AddToQuoteComponent extends Component {
  requiredRefs = ['addToQuoteButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToQuoteButton.disabled = true;
  }

  enable() {
    this.refs.addToQuoteButton.disabled = false;
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    if (button.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToQuoteAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.animateFlyToQuote(button);
      }
      this.#animateSingleButton(button);
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  /**
   * @param {HTMLButtonElement} addToQuoteButton
   */
  animateFlyToQuote(addToQuoteButton) {
    return this.#animateFlyToQuote(addToQuoteButton);
  }

  /**
   * @param {HTMLButtonElement} addToQuoteButton
   */
  #animateFlyToQuote(addToQuoteButton) {
    const quoteIcon = document.querySelector('[data-quote-icon]');
    const image = this.dataset.productVariantMedia;

    if (!quoteIcon || !addToQuoteButton || !image) return;

    const flyToQuoteElement = /** @type {FlyToQuote} */ (document.createElement('fly-to-cart'));
    const flyToQuoteClass = addToQuoteButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToQuoteElement.classList.add(flyToQuoteClass);
    flyToQuoteElement.style.setProperty('background-image', `url(${image})`);
    flyToQuoteElement.style.setProperty('--start-opacity', '0');
    flyToQuoteElement.source = addToQuoteButton;
    flyToQuoteElement.destination = quoteIcon;

    document.body.appendChild(flyToQuoteElement);
  }

  /**
   * @param {HTMLButtonElement} addToQuoteButton
   */
  animateButton(addToQuoteButton) {
    return this.#animateSingleButton(addToQuoteButton);
  }

  /**
   * @param {HTMLButtonElement} addToQuoteButton
   */
  #animateSingleButton = async (addToQuoteButton) => {
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToQuoteButton.dataset.added !== 'true') {
      addToQuoteButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToQuoteButton);

    const timeoutId = setTimeout(() => {
      addToQuoteButton.removeAttribute('data-added');
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };

  animateAddToQuote = async function () {
    const button = this.refs.addToQuoteButton;
    const buttons = Array.isArray(button) ? button : button ? [button] : [];

    for (const addToQuoteButton of buttons) {
      await this.#animateSingleButton(addToQuoteButton);
    }
  };
}

/**
 * @typedef {object} QuoteFormRefs
 * @property {HTMLElement} [liveRegion]
 * @property {AddToQuoteComponent} [addToQuoteButtonContainer]
 * @property {HTMLInputElement} [variantId]
 * @extends {Component<QuoteFormRefs>}
 */
export class QuoteFormComponent extends Component {
  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quoteUpdate, this.#onQuoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quoteUpdate, this.#onQuoteUpdate);

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
  }

  /** @param {QuoteAddEvent | QuoteUpdateEvent} event */
  #onQuoteUpdate = (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'quote-form-component') return;
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    this.#processAddToQuote();
  }

  /**
   * Adds the current variant to the quote without submitting the form.
   * @param {MouseEvent} [event]
   */
  handleQuickAddToQuote = (event) => {
    event?.preventDefault();
    this.#processAddToQuote(event);
  };

  /** @returns {import('@theme/quote-pricing').QuotePricingConfig} */
  #readPricingConfig() {
    return {
      moneyFormat: this.getAttribute('data-money-format') || '',
      currency: this.getAttribute('data-currency') || '',
      gstInclusivePricing: this.getAttribute('data-gst-inclusive-pricing') === 'true',
      gstPercentage: Number(this.getAttribute('data-gst-percentage')) || 10,
      currencyCodeBeforePrice:
        this.getAttribute('data-currency-code-before-price') === 'true',
    };
  }

  /** @param {Event} [event] */
  #processAddToQuote(event) {
    const form = this.querySelector('form');
    if (!form) return;

    const allAddToQuoteContainers = /** @type {NodeListOf<AddToQuoteComponent>} */ (
      this.querySelectorAll('add-to-quote-component')
    );

    const anyButtonDisabled = Array.from(allAddToQuoteContainers).some((container) => {
      const button = container.refs.addToQuoteButton;
      const buttons = Array.isArray(button) ? button : button ? [button] : [];
      return buttons.some((addToQuoteButton) => addToQuoteButton.disabled);
    });

    if (anyButtonDisabled) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    if (!variantId) {
      this.dispatchEvent(new QuoteErrorEvent(this.id, 'Variant is required'));
      return;
    }

    /** @type {{ height?: string, position?: string }} */
    let metaTags = {};
    const metaTagsRaw = this.dataset.productMetaTags?.trim();

    if (metaTagsRaw) {
      try {
        metaTags = JSON.parse(metaTagsRaw);
      } catch {
        metaTags = {};
      }
    }

    const variantPriceCents = Number(this.getAttribute('data-variant-price')) || 0;
    const priceDisplayFromDataset =
      this.getAttribute('data-variant-price-display')?.trim() || '';

    const quote = QuoteStorage.addItem(
      {
        id: String(variantId),
        productId: this.dataset.productId || '',
        quantity,
        title: this.dataset.productTitle || '',
        variantTitle: this.dataset.variantTitle || '',
        image: this.dataset.productVariantMedia || '',
        sku: this.dataset.variantSku || '',
        handle: this.dataset.productHandle || '',
        metaHeight: metaTags.height || '',
        metaPosition: metaTags.position || '',
        price: variantPriceCents,
        priceDisplay:
          priceDisplayFromDataset ||
          formatQuotePrice(variantPriceCents, this.#readPricingConfig()),
      },
      quantity
    );

    const addedText = Theme.translations.addedToQuote || Theme.translations.added;
    this.#setLiveRegionText(addedText);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      this.#clearLiveRegionText();
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);

    for (const container of allAddToQuoteContainers) {
      const trigger = event?.currentTarget;
      const addButton = form.querySelector('.quick-add__button--quote');

      if (trigger instanceof HTMLButtonElement) {
        if (
          container.dataset.addToQuoteAnimation === 'true' &&
          !trigger.closest('.quick-add-modal')
        ) {
          container.animateFlyToQuote(trigger);
        }
        container.animateButton(trigger);
      } else if (addButton instanceof HTMLButtonElement) {
        if (
          container.dataset.addToQuoteAnimation === 'true' &&
          !addButton.closest('.quick-add-modal')
        ) {
          container.animateFlyToQuote(addButton);
        }
        container.animateButton(addButton);
      } else {
        container.animateAddToQuote?.();
      }
    }

    this.dispatchEvent(
      new QuoteAddEvent(quote, this.id, {
        source: 'quote-form-component',
        itemCount: quantity,
        productId: this.dataset.productId,
        variantId: String(variantId),
      })
    );

    if (event) {
      event.stopPropagation();
    }
  }

  /** @param {string} text */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    if (!liveRegion) return;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    if (!liveRegion) return;
    liveRegion.textContent = '';
  }
}

if (!customElements.get('add-to-quote-component')) {
  customElements.define('add-to-quote-component', AddToQuoteComponent);
}

if (!customElements.get('quote-form-component')) {
  customElements.define('quote-form-component', QuoteFormComponent);
}
